<template>
  <Transition name="fade">
    <div v-if="currentHint" ref="hintContainer" class="hintContainer hintfade">
      <v-card dark color="#5E00EB" :width="HINT_CARD_WIDTH">
        <v-card-title class="pb-1 hintTitle">{{ currentHint.Title }}</v-card-title>
        <v-img
          v-if="currentHint.Media && currentHint.Media.Type === 'image'"
          contain
          :src="currentHint.Media.Link"
        ></v-img>
        <v-card-text class="pa-0" v-if="currentHint.Media && currentHint.Media.Type === 'video'">
          <iframe
            width="300"
            allowfullscreen
            frameborder="0"
            :src="currentHint.Media.Type === 'video' ? `https://www.youtube.com/embed/${currentHint.Media.Link}` : currentHint.Media.Link"
          >
          </iframe>
        </v-card-text>
        <v-card-text class="py-1 hintDescription">{{ currentHint.Description }}</v-card-text>
        <v-card-text v-if="currentHint.LearnMore" class="py-1 hintDescription">
          <a :href="currentHint.LearnMore" target="_blank" style="color: white; font-style: italic;">Learn More</a>
        </v-card-text>
        <v-card-actions>
          <span class="dontShowHintWrapper" @click="handleDontShowHints">Don't show me hints</span>
          <v-spacer></v-spacer>
          <v-row class="wrap" justify="end" no-gutters>
            <v-btn
              light
              color="white"
              @click="handleButtonClick()"
              >{{ currentHint.ButtonText ? currentHint.ButtonText : 'Got It!' }}</v-btn
            >
          </v-row>
        </v-card-actions>
      </v-card>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'hintSystem',
  data() {
    return {
      HINT_CARD_WIDTH: 300,
      hintElement: undefined,
      hintTargetElementRect: undefined,
      currentHint: undefined,
      hintsQueue: [],
      isComponentHint: false,
      hintsViewedCount: 0,
      dontShowHints: false,
      optimizations: {},
      hintsToExclude: new Set(),
      hintsOnAction: [],

      // hints are shown on actions by fems
      // {
      //   fems1: [action1, action2 ...],
      //   fems2: [action1, action2 ...],
      // } 
      shownActionHintsList: {},
    }
  },
  mounted() {
    this.guibus_subscribe('addToQueue:userHints', this.addHintsToQueue)
    this.guibus_subscribe('show:adacadoHint', this.showSelectedHint)
    this.guibus_subscribe('show:hintOnAction', this.showHintOnAction)

    this.AccountInterface?.get(account => {
      this.optimizations = account.Properties.signupOptimizations || {}
      // Hints to exclude depending on intro survey answers
      // check https://docs.google.com/spreadsheets/d/1vlBKnWelcVpcy3PlPoSUMeRUY2KsWd2tfeVCOai7Tdc/edit#gid=0 for more info
      if (this.optimizations.version === 2) {
        if (this.optimizations.answers[0] === 'agency') {
          this.hintsToExclude.add(2)
        }
        if (this.optimizations.answers[0] === 'personal') {
          this.hintsToExclude.add(22)
        }
        if (this.optimizations.answers[2] === 'advanced') {
          this.hintsToExclude.add(2)
          this.hintsToExclude.add(5)
          this.hintsToExclude.add(17)
        }
        if (this.optimizations.answers[3] === 'staticCreative') {
          this.hintsToExclude.add(8)
          this.hintsToExclude.add(11)
          this.hintsToExclude.add(17)
        }
      }
    })
  },
  computed: {
    HintInterface() {
      return window.adacado.bscs.HintInterfaceFactory()
    },
    AccountInterface() {
      return window.adacado.bscs?.AccountInterfaceFactory()
    },
  },
  methods: {
    handleDontShowHints() {
      this.HintInterface.setDontShowHints()
      this.dontShowHints = true
      this.resetCurrentHint()
    },
    handleButtonClick() {
      if (this.isComponentHint) {
        this.resetCurrentHint()
      } else {
        this.currentHint.Viewed = true
        this.removeDependencies(this.currentHint.HintID)
        this.guibus_publish('dispatch:beacon', { eventId: `hint_${this.currentHint.HintID}_viewed` })
        this.HintInterface.writeAccountHintViewed(this.currentHint.HintID)
        this.processQueue()
      }
    },
    removeDependencies(hintId) {
      this.hintsQueue.forEach(hint => {
        if (hint.Dependencies?.includes(hintId)) {
          hint.Dependencies = hint.Dependencies.filter(dep => dep !== hintId)
        }
      })
    },
    handleNotNowClick() {
      // jyelle - this function was used for the trust pilot hint (removed on 2023-01-31) and is no longer being used for any other hints, but
      // can still be used in the future for any other type of recurring hint/reminder, so I'm leaving it in
      this.currentHint.Viewed = true
      this.guibus_publish('dispatch:beacon', { eventId: `hint_${this.currentHint.HintID}_not-now` })
      this.HintInterface.writeAccountHintNotNow(this.currentHint.HintID)
      this.processQueue()
    },
    showHintOnAction(action) {
      const femsName = window.location.href.split('/')[3]

      // display hint only once per action,fems on the page
      if (this.shownActionHintsList[femsName]?.includes(action)) {
        return
      }
      this.shownActionHintsList[femsName] = this.shownActionHintsList[femsName] || []
      this.shownActionHintsList[femsName].push(action)
      
      const hintsByAction = this.hintsOnAction.filter(hint => hint.HintOnAction === action)
      hintsByAction.forEach(hint => {
        this.showSelectedHint(hint, false)
      })
    },
    showSelectedHint(hint, isComponentHint = true) {
      this.isComponentHint = isComponentHint
      this.resetCurrentHint()
      this.$nextTick(() => {
        this.currentHint = hint
        this.hintTargetElementRect = document
          .querySelectorAll(`.${this.currentHint.TargetElementClass}`)[0]
          ?.getBoundingClientRect()

        // if no element found that relates to current hint, skip hint and continue processing
        if (!this.hintTargetElementRect && this.currentHint.HintPosition !== 'window-center') {
          this.currentHint.Viewed = true
          this.resetCurrentHint()
          return
        }

        this.$nextTick(() => {
          this.setHintPosition()
        })
      })
    },
    addHintsToQueue(hints) {
      // filter out hints that will be executed on action
      this.hintsOnAction.push(...hints.filter(h => h.HintOnAction != ""))
      hints = hints.filter(h => h.HintOnAction === "")

      this.hintsQueue = this.hintsQueue.concat(hints)
      if (!this.currentHint) {
        this.processQueue()
      }
    },
    processQueue() {
      this.resetCurrentHint()
      if (this.dontShowHints || this.hintsViewedCount >= 3) return
      this.$nextTick(() => {
        for (const hint of this.hintsQueue) {
          // do not show hint if already viewed or if hint has dependency which has not been viewed yet
          if (
            hint.Viewed ||
            (!hint.Viewed &&
              hint.Dependencies?.length &&
              !this.hintsQueue.find(h => h.HintID === hint.Dependencies[0])?.Viewed)
          ) {
            continue
          }
          this.currentHint = hint
          break
        }
        // if no hint set at end of loop, must be finished with all hints, close hint system
        if (!this.currentHint) {
          this.resetCurrentHint()
          return
        }
        this.hintTargetElementRect = document
          .querySelectorAll(`.${this.currentHint.TargetElementClass}`)[0]
          ?.getBoundingClientRect()
        // if no element found that relates to current hint, skip hint and continue processing
        if ((!this.hintTargetElementRect && this.currentHint.HintPosition !== 'window-center') || this.hintsToExclude.has(this.currentHint.HintID)) {
          this.currentHint.Viewed = true
          this.processQueue()
          return
        }

        this.$nextTick(() => {
          this.setHintPosition()
          this.hintsViewedCount++
        })
      })
    },
    // position hint and indicator arrow depending on hint position (arrow indicator directions are opposite of position,
    // eg. a hint positioned 'top' is above the element it is indicating, therefore it needs a 'bottom' facing arrow)
    setHintPosition() {
      // Set hint above campaign guide (consequently header) only for first hint
      if (this.currentHint.HintID === 1) {
        this.$refs.hintContainer.style.zIndex = "1000"
      } else {
        this.$refs.hintContainer.style.zIndex = "998"
      }
      switch (this.currentHint.HintPosition) {
        case 'window-center':
          this.$refs.hintContainer.style.top = `calc(100vh / 2 - ${this.$refs.hintContainer.offsetHeight / 2}px)`
          this.$refs.hintContainer.style.left = `calc(100vw / 2 - ${this.$refs.hintContainer.offsetWidth / 2}px)`
          this.$refs.hintContainer.classList.add('noArrow')
          break
        case 'top':
          this.$refs.hintContainer.style.bottom = `calc(100vh - ${this.hintTargetElementRect.top -
            18}px)`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left}px`
          this.$refs.hintContainer.classList.add('bottomArrow')
          break
        case 'bottom':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.bottom + 25}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left}px`
          this.$refs.hintContainer.classList.add('topArrow')
          break
        case 'left':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.top}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.left -
            this.HINT_CARD_WIDTH -
            18}px`
          this.$refs.hintContainer.classList.add('rightArrow')
          break
        case 'right':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.top}px`
          this.$refs.hintContainer.style.left = `${this.hintTargetElementRect.right + 18}px`
          this.$refs.hintContainer.classList.add('leftArrow')
          break
        // all hints other than first hint will be below header in z-index, thus this position is necessary
        // for hints that need to indicate header elements on the far right
        case 'bottom-right':
          this.$refs.hintContainer.style.top = `${this.hintTargetElementRect.bottom + 25}px`
          this.$refs.hintContainer.style.right = `calc(100vw - ${this.hintTargetElementRect.right + 18}px)`
          this.$refs.hintContainer.classList.add('topRightArrow')
          break
      }
    },
    resetCurrentHint() {
      this.currentHint = undefined
      if (this.$refs.hintContainer) {
        this.$refs.hintContainer.style.top = ''
        this.$refs.hintContainer.style.bottom = ''
        this.$refs.hintContainer.style.right = ''
        // This offset is mainly for the last hint as animation fade is
        // still applied with the component placed in the corner. Instead
        // we just move the component out of view while it transitions out.
        this.$refs.hintContainer.style.left = '-1500px'
      }
      this.hintTargetElementRect = undefined
      this.$refs.hintContainer?.classList.remove('topArrow')
      this.$refs.hintContainer?.classList.remove('bottomArrow')
      this.$refs.hintContainer?.classList.remove('leftArrow')
      this.$refs.hintContainer?.classList.remove('rightArrow')
      this.$refs.hintContainer?.classList.remove('topRightArrow')
      this.$refs.hintContainer?.classList.remove('noArrow')
    },
  },
  beforeDestroy() {
    this.guibus_unsubscribe('addToQueue:userHints', this.addHintsToQueue)
    this.guibus_unsubscribe('show:adacadoHint', this.showSelectedHint)
  }
}
</script>

<style lang="scss" scoped>
.hintContainer {
  position: fixed;
  color: white;
  .hintTitle {
    word-break: break-word;
  }
  .hintDescription {
    max-height: 250px;
    overflow-y: auto;
    color: white !important;
  }
  &:after {
    content: ' ';
    position: absolute;
    border-width: 10px;
    border-style: solid;
    box-shadow: -2px -2px 2px 0 rgba(178, 178, 178, 0.4);
    color: #5E00EB;
  }
  &.noArrow:after {
    display: none;
  }
  &.rightArrow:after {
    top: 2.5%;
    left: 100%;
    transform: rotate(135deg);
    margin-left: -11px;
  }
  &.leftArrow:after {
    top: 2.5%;
    right: 100%;
    transform: rotate(315deg);
    margin-right: -11px;
  }
  &.topArrow:after {
    bottom: 100%;
    left: 5%;
    transform: rotate(45deg);
    margin-bottom: -11px;
  }
  &.bottomArrow:after {
    top: 100%;
    left: 5%;
    transform: rotate(225deg);
    margin-top: -11px;
  }
  &.topRightArrow:after {
    bottom: 100%;
    right: 5%;
    transform: rotate(45deg);
    margin-bottom: -11px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.dontShowHintWrapper {
  font-size: 12px;
  font-weight: bold;
  font-style: italic;
  color: #ffb3ff;
  margin-left: 8px;
  cursor: pointer;
}
</style>
